/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx, cssMap, cssMap as unboundCssMap } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled';
import {
	MetadataBlock,
	SmartLinkTheme,
	TitleBlock,
	SnippetBlock,
	ElementName,
} from '@atlaskit/smart-card';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import type { KnowledgeCardSource } from './types';

const styles = cssMap({
	container: {},
});

const unboundStyles = unboundCssMap({
	titleBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		"[data-smart-element='Title']": {
			font: token('font.body'),
			fontWeight: token('font.weight.semibold'),
			color: token('color.text'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Override visited link color
		"a:visited [data-smart-element='Title']": {
			color: token('color.text'),
		},
	},
	metadataBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'& span': {
			color: token('color.text.subtlest'),
		},
	},
	snippetBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'& span': {
			color: token('color.text.subtle'),
		},
	},
});

interface SourcesSidePanelCardProps {
	index: number;
	onClickHandler: (params: any) => void;
	onError?: (error: any) => void;
	searchSessionId: string;
	source: KnowledgeCardSource;
}

export const SourcesSidePanelCard = ({
	source,
	index,
	onClickHandler,
	onError,
}: SourcesSidePanelCardProps): React.JSX.Element => {
	const handleClick = (e: React.MouseEvent) => {
		const isAuxClick = e.button === 1;
		onClickHandler?.({
			index,
			source,
			sourceCardType: 'side-panel-source',
			isAuxClick,
		});
		e.preventDefault();
		window.open(source.url, '_blank', 'noopener, noreferrer');
	};

	return (
		<Box xcss={styles.container} onAuxClick={handleClick}>
			<Card
				appearance="block"
				showHoverPreview={true}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideElevation: true,
					hidePadding: true,
				}}
				url={source.url}
				onClick={handleClick}
				onError={onError || (() => {})}
			>
				<TitleBlock css={unboundStyles.titleBlockCss} maxLines={1} />
				<MetadataBlock
					css={unboundStyles.metadataBlockCss}
					primary={[
						{ name: ElementName.ModifiedOn },
						{ name: ElementName.ModifiedBy },
						{ name: ElementName.OwnedBy },
					]}
					maxLines={1}
				/>
				<SnippetBlock css={unboundStyles.snippetBlockCss} maxLines={2} />
			</Card>
		</Box>
	);
};
